import { TextSelection } from 'prosemirror-state';
import { Slice, Fragment } from 'prosemirror-model';
import { render, h } from 'preact';
import classNames from 'classnames';
import { SHADOWDOM_CLASS, RESIZER_CLASS, IMAGE_MIN_WIDTH, IMAGE_INFO_DISPLAY_MIN_HEIGHT_SIZE, IMAGE_OFFSET, POPOVER_CLASS, IMAGE_MAX_HEIGHT } from '../consts/index.js';
import throttle from 'lodash.throttle';

function getContentWidth($elem) {
  const computedStyle = window.getComputedStyle($elem);
  const width = +computedStyle.width.replace('px', '');
  const paddingLeft = +computedStyle.paddingLeft.replace('px', '');
  const paddingRight = +computedStyle.paddingRight.replace('px', '');
  return width - paddingLeft - paddingRight;
} // 向上寻找指定PM节点

function findParentNode(path, assert) {
  for (let i = path.length - 1; i >= 0; i--) {
    if (assert(path[i])) {
      return {
        node: path[i],
        pos: path[i - 1]
      };
    }
  }

  return null;
} // 向上寻找指定DOM节点

const findParentDOM = (targetDOM, assert) => {
  let dom = targetDOM;

  while (true) {
    if (!dom || dom.classList && dom.classList.contains('ProseMirror')) return false;
    if (assert(dom)) return dom;
    dom = dom.parentNode;
  }
}; // 向上寻找最近的display为block的DOM节点

const findParentBlockNode = targetDOM => {
  let dom = targetDOM.parentNode;
  let flag = 0;

  while (flag < 40) {
    if (!dom || dom.classList && dom.classList.contains('ProseMirror')) return false;
    if (['block', 'flex'].includes(getComputedStyle(dom).display)) return dom;
    dom = dom.parentNode;
    flag++;
  }
}; // 修改指定位置间的节点属性

function setBetweenAttrs(from, to, tr, assert, attrsGetter) {
  tr.doc.nodesBetween(from, to, (node, pos, parent) => {
    if (assert(node, pos, parent)) {
      const newAttrs = attrsGetter(node.attrs);

      try {
        tr.setNodeMarkup(pos, null, newAttrs);
      } catch (error) {
        console.error(error);
      }
    }
  });
}
function getInsertBlockSlice(state, node) {
  const {
    schema,
    selection,
    doc
  } = state;
  const {
    paragraph
  } = schema.nodes;
  const {
    $from,
    $to,
    to
  } = selection;
  const beforeNode = $from.nodeBefore;
  const afterNode = $to.nodeAfter;
  const fromNodeGrandParent = $from.node(-1);
  const fromNodeParent = $from.node(); // 光标在空行时

  if ((!beforeNode || !beforeNode.isText && !beforeNode.isTextblock && !beforeNode.isInline) && (!afterNode || !afterNode.isText && !afterNode.isTextblock && !afterNode.isInline)) {
    const nodes = [node, paragraph.create()]; // 是否在文档尾部后单元格尾部

    const isInTail = to === doc.content.size - 1 || (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;
    if (isInTail) return new Slice(Fragment.from(nodes), 0, 0); // 后面一行是否为文本块（段落、标题）

    const nextBlockNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;

    if (nextBlockNode && !nextBlockNode.isTextblock && !nextBlockNode.isText) {
      return new Slice(Fragment.from(nodes), 0, 0);
    }
  }

  return new Slice(Fragment.from([node]), 0, 0);
} // 插入块处理

function insertBlock(state, node, headNoSpace) {
  const {
    tr,
    schema,
    selection
  } = state;
  const {
    table,
    code_block,
    status,
    note,
    note_title,
    collapse_title
  } = schema.nodes;
  const {
    to,
    $from
  } = selection;
  const nodeBefore = $from.nodeBefore; // 光标前相邻节点

  const parentNode = $from.parent;
  const textBlocks = [code_block, status, note_title, collapse_title]; // eslint-disable-line

  if (textBlocks.includes(parentNode.type)) return false;
  let transaction = tr.setStoredMarks([]); // 清除格式

  const slice = getInsertBlockSlice(state, node, headNoSpace);
  transaction = transaction.replaceSelection(slice); // 移动光标位置

  let newPos = nodeBefore ? 2 : 0;
  newPos = selection.constructor.name === 'GapCursor' ? to : to + newPos;

  if (node.type.isLeaf) {
    newPos += 1;
  } else if (node.type === note) {
    newPos += 4;
  } else if (node.type === table) {
    newPos += 3;
  }

  transaction = transaction.setSelection(TextSelection.create(transaction.doc, newPos));
  return {
    transaction,
    newPos
  };
} // 类型是否可以插入富文本

function canInsertTypes(state) {
  const {
    schema,
    selection
  } = state;
  const {
    $from
  } = selection;
  const {
    status,
    code_block,
    note_title,
    collapse_title
  } = schema.nodes;
  const parentNode = $from.parent;
  const texBlocks = [status, code_block, note_title, collapse_title]; // eslint-disable-line

  return !texBlocks.includes(parentNode.type);
} // 是否为其子节点

const isChild = (child, parent) => {
  let is = false;

  const dfs = node => {
    if (is) return;
    const content = node.content.content;

    for (let i = 0; i < content.length; i++) {
      const n = content[i];

      if (n === child) {
        is = true;
        break;
      }

      dfs(n);
    }
  };

  dfs(parent);
  return is;
}; // 获取块节点

const getBlockNode = (state, from, to) => {
  const {
    tr,
    selection
  } = state;
  const fromParentNode = selection.$from.node(-1);
  const newFrom = from < 0 ? 0 : from;
  const newTo = to > tr.doc.content.size ? tr.doc.content.size : to;
  let is = null;
  tr.doc.nodesBetween(newFrom, newTo, node => {
    const nodeType = node.type;
    if (nodeType && nodeType.spec.insertBlock && !isChild(fromParentNode, node)) is = node;
    return !is;
  });
  return is;
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css = ".ct-toastr{position:relative;display:flex;flex-direction:row;flex-wrap:wrap;background:#fff;border-radius:4px;box-shadow:0 4px 12px rgba(0,0,0,.15);padding:10px;width:-webkit-fit-content;width:-moz-fit-content;width:fit-content;left:50%;top:55px;-webkit-transform:translateX(-50%);transform:translateX(-50%);z-index:9000}.ct-toastr .ct-toastr-success{margin-right:10px;width:16px;height:16px;background:url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTYuOTQgOC44NjVMNC44MDEgNi43MjhhMS4yMDMgMS4yMDMgMCAwIDAtMS42OTQuMDAzIDEuMTk1IDEuMTk1IDAgMCAwLS4wMDMgMS42OTRMNi4wOCAxMS40Yy4yMzUuMjM1LjU0OC4zNTEuODYxLjM0Ny4zMTIuMDA0LjYyNC0uMTEzLjg2MS0uMzVMMTIuODk4IDYuM2ExLjIwMyAxLjIwMyAwIDAgMC0uMDAyLTEuNjk0IDEuMTk1IDEuMTk1IDAgMCAwLTEuNjk1LS4wMDNMNi45NCA4Ljg2NXpNMTYgOGMwIDEuNDUxLS4zNTggMi43OS0xLjA3MyA0LjAxNmE3Ljk2NCA3Ljk2NCAwIDAgMS0yLjkxMSAyLjkxMUE3LjgyNiA3LjgyNiAwIDAgMSA4IDE2YTcuODI2IDcuODI2IDAgMCAxLTQuMDE2LTEuMDczIDcuOTY0IDcuOTY0IDAgMCAxLTIuOTExLTIuOTExQTcuODI2IDcuODI2IDAgMCAxIDAgOGMwLTEuNDUxLjM1OC0yLjc5IDEuMDczLTQuMDE2YTcuOTY0IDcuOTY0IDAgMCAxIDIuOTExLTIuOTExQTcuODI2IDcuODI2IDAgMCAxIDggMGMxLjQ1MSAwIDIuNzkuMzU4IDQuMDE2IDEuMDczYTcuOTY0IDcuOTY0IDAgMCAxIDIuOTExIDIuOTExQTcuODI2IDcuODI2IDAgMCAxIDE2IDh6IiBmaWxsPSIjNzBCMTIzIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3N2Zz4=\") no-repeat 0}.ct-toastr .ct-toastr-error{margin-right:10px;width:16px;height:16px;background:url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTggMGE4IDggMCAxIDEgMCAxNkE4IDggMCAwIDEgOCAwek0zLjIgOGMwIC42NjcuNTM2IDEuMiAxLjE5NiAxLjJoNy4yMDhjLjY1MiAwIDEuMTk2LS41MzcgMS4xOTYtMS4yIDAtLjY2Ny0uNTM2LTEuMi0xLjE5Ni0xLjJINC4zOTZDMy43NDQgNi44IDMuMiA3LjMzNyAzLjIgOHoiIGZpbGw9IiNGMDVFNTAiIGZpbGwtcnVsZT0iZXZlbm9kZCIvPjwvc3ZnPg==\") no-repeat 0}";
styleInject(css);

const uuid = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split("");
  const id = [];

  for (let i = 0; i < 8; i++) {
    id.push(chars[parseInt(`${Math.random() * chars.length}`, 10)]);
  }

  return id.join("");
};

const createContainer = () => {
  const dom = document.createElement('div');
  dom.setAttribute('class', 'ct-toast-container');
  dom.setAttribute('style', 'position:absolute;top:0;left:0;width:100%');
  document.body.appendChild(dom);
  return dom;
};

const clearToast = (id, container, delay) => {
  setTimeout(() => {
    const dom = document.querySelector(`#${id}`);
    if (!dom) return;
    render(null, container, dom);
  }, delay * 1000);
};

const ToastrContent = props => {
  return h("div", {
    className: "ct-toastr",
    id: props.id
  }, h("div", {
    className: classNames({
      [`ct-toastr-${props.type}`]: true
    })
  }), h("span", null, props.text));
};

class Toastr {
  static success(str, delay = 1.5) {
    let dom = document.querySelector('.ct-toast-container');
    if (!dom) dom = createContainer();
    const id = uuid();
    render(h(ToastrContent, {
      id: id,
      type: "success",
      text: str
    }), dom);
    clearToast(id, dom, delay);
  }

  static error(str, delay = 1.5) {
    let dom = document.querySelector('.ct-toast-container');
    if (!dom) dom = createContainer();
    const id = uuid();
    render(h(ToastrContent, {
      id: id,
      type: "error",
      text: str
    }), dom);
    clearToast(id, dom, delay);
  }

}

const config = window.__CLIENT_CONFIG__ || {};

function getSystemInfo() {
  // browser
  const nVer = navigator.appVersion;
  const nAgt = navigator.userAgent;
  let browser = navigator.appName;
  let version = `${parseFloat(navigator.appVersion)}`;
  let nameOffset, verOffset, ix;
  const isMobile = nAgt.toLowerCase().indexOf("mobile") !== -1; // Opera

  if ((verOffset = nAgt.indexOf("Opera")) !== -1) {
    browser = "Opera";
    version = nAgt.substring(verOffset + 6);

    if ((verOffset = nAgt.indexOf("Version")) !== -1) {
      version = nAgt.substring(verOffset + 8);
    }
  } // Opera Next


  if ((verOffset = nAgt.indexOf("OPR")) !== -1) {
    browser = "Opera";
    version = nAgt.substring(verOffset + 4); // Edge
  } else if ((verOffset = nAgt.indexOf("Edge")) !== -1) {
    browser = "Microsoft Edge";
    version = nAgt.substring(verOffset + 5); // MSIE
  } else if ((verOffset = nAgt.indexOf("MSIE")) !== -1) {
    browser = "Microsoft Internet Explorer";
    version = nAgt.substring(verOffset + 5); // Chrome
  } else if ((verOffset = nAgt.indexOf("Chrome")) !== -1) {
    browser = "Chrome";
    version = nAgt.substring(verOffset + 7); // Safari
  } else if ((verOffset = nAgt.indexOf("Safari")) !== -1) {
    browser = "Safari";
    version = nAgt.substring(verOffset + 7);

    if ((verOffset = nAgt.indexOf("Version")) !== -1) {
      version = nAgt.substring(verOffset + 8);
    } // Firefox

  } else if ((verOffset = nAgt.indexOf("Firefox")) !== -1) {
    browser = "Firefox";
    version = nAgt.substring(verOffset + 8); // MSIE 11+
  } else if (nAgt.indexOf("Trident/") !== -1) {
    browser = "Microsoft Internet Explorer";
    version = nAgt.substring(nAgt.indexOf("rv:") + 3); // Other browsers
  } else if ((nameOffset = nAgt.lastIndexOf(" ") + 1) < (verOffset = nAgt.lastIndexOf("/"))) {
    browser = nAgt.substring(nameOffset, verOffset);
    version = nAgt.substring(verOffset + 1);

    if (browser.toLowerCase() === browser.toUpperCase()) {
      browser = navigator.appName;
    }
  } // trim the version string


  if ((ix = version.indexOf(";")) !== -1) {
    version = version.substring(0, ix);
  }

  if ((ix = version.indexOf(" ")) !== -1) {
    version = version.substring(0, ix);
  }

  if ((ix = version.indexOf(")")) !== -1) {
    version = version.substring(0, ix);
  } // system


  let os = "unknown";
  const clientStrings = [{
    s: "Windows 10",
    r: /(Windows 10.0|Windows NT 10.0)/
  }, {
    s: "Windows 8.1",
    r: /(Windows 8.1|Windows NT 6.3)/
  }, {
    s: "Windows 8",
    r: /(Windows 8|Windows NT 6.2)/
  }, {
    s: "Windows 7",
    r: /(Windows 7|Windows NT 6.1)/
  }, {
    s: "Windows Vista",
    r: /Windows NT 6.0/
  }, {
    s: "Windows Server 2003",
    r: /Windows NT 5.2/
  }, {
    s: "Windows XP",
    r: /(Windows NT 5.1|Windows XP)/
  }, {
    s: "Windows 2000",
    r: /(Windows NT 5.0|Windows 2000)/
  }, {
    s: "Windows ME",
    r: /(Win 9x 4.90|Windows ME)/
  }, {
    s: "Windows 98",
    r: /(Windows 98|Win98)/
  }, {
    s: "Windows 95",
    r: /(Windows 95|Win95|Windows_95)/
  }, {
    s: "Windows NT 4.0",
    r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/
  }, {
    s: "Windows CE",
    r: /Windows CE/
  }, {
    s: "Windows 3.11",
    r: /Win16/
  }, {
    s: "Android",
    r: /Android/
  }, {
    s: "Open BSD",
    r: /OpenBSD/
  }, {
    s: "Sun OS",
    r: /SunOS/
  }, {
    s: "Linux",
    r: /(Linux|X11)/
  }, {
    s: "iOS",
    r: /(iPhone|iPad|iPod)/
  }, {
    s: "Mac OS X",
    r: /Mac OS X/
  }, {
    s: "Mac OS",
    r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/
  }, {
    s: "QNX",
    r: /QNX/
  }, {
    s: "UNIX",
    r: /UNIX/
  }, {
    s: "BeOS",
    r: /BeOS/
  }, {
    s: "OS/2",
    r: /OS\/2/
  }, {
    s: "Search Bot",
    r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/
  }];

  for (const id in clientStrings) {
    const cs = clientStrings[id];

    if (cs.r.test(nAgt)) {
      os = cs.s;
      break;
    }
  }

  let osVersion = "unknown";

  if (/Windows/.test(os)) {
    osVersion = /Windows (.*)/.exec(os)[1];
    os = "Windows";
  }

  switch (os) {
    case "Mac OS X":
      osVersion = /Mac OS X (10[._\d]+)/.exec(nAgt)[1];
      break;

    case "Android":
      osVersion = /Android ([._\d]+)/.exec(nAgt)[1];
      break;

    case "iOS":
      osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer);
      osVersion = `${osVersion[1]}.${osVersion[2]}.${osVersion[3] | 0}`;
      break;

    default:
      break;
  }

  return {
    browser: browser || "unknown",
    browserVersion: version || "unknown",
    os: os || "unknown",
    osVersion: osVersion || "unknown",
    isMobile
  };
} // 键盘上的全部可打印键

function isPrintableKey(evt) {
  const key = evt.which || evt.keyCode;
  return !(evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey) && (key === 32 || // 空格
  key >= 48 && key <= 57 || // 数字
  key >= 65 && key <= 90 || // 字母
  key >= 96 && key <= 105 || // 小键盘数字
  key === 106 || key >= 186 && key <= 192 || key >= 219 || key <= 222 // 其他符号键
  );
} // 计算文件大小

function compute(origin) {
  if (origin >= 1024 * 1024) {
    return `${(origin / (1024 * 1024)).toFixed(2)}MB`;
  } else if (origin >= 1024) {
    return `${(origin / 1024).toFixed(2)}KB`;
  }

  return `${origin || 0}B`;
} // getBoundingClientRect().left

const getTargetLeft = target => {
  let left = 0;
  let dom = target;

  while (dom != null) {
    const offsetParent = dom.offsetParent;
    let parent = dom.parentNode;
    left += dom.offsetLeft - dom.scrollLeft;

    while (parent && parent !== offsetParent) {
      const pOffsetLeft = parent.scrollLeft;

      if (pOffsetLeft) {
        left -= pOffsetLeft;
      }

      parent = parent.parentNode;
    }

    dom = offsetParent;
  }

  return left;
}; // 配置校验

const checkConfig = (config, rule) => {
  // tslint:disable-next-line: forin
  for (const key in rule) {
    const value = config[key];
    const type = rule[key];

    if (!value) {
      throw new TypeError(`Missing configuration item ${key}`);
    }

    if (typeof type === "string") {
      if (Object.prototype.toString.call(value).slice(8, -1) !== rule[key]) {
        throw new TypeError(`Configuration item ${key} type must be ${rule[key]}`);
      }
    } else if (!(value instanceof type)) {
      throw new TypeError(`Configuration item ${key} type must be ${rule[key]}`);
    }
  }
}; // uuid

function uuid$1() {
  const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz".split("");
  const id = [];

  for (let i = 0; i < 8; i++) {
    id.push(chars[parseInt(`${Math.random() * chars.length}`, 10)]);
  }

  return id.join("");
}
const getSizeFromSvg = svgStr => {
  let width = 0;
  let height = 0;
  const widthRes = svgStr.match(/width="([^"]+)"/);
  const heightRes = svgStr.match(/height="([^"]+)"/);

  if (widthRes && heightRes) {
    width = parseInt(widthRes[1], 10);
    height = parseInt(heightRes[1], 10);
  }

  if (!width || !height) {
    const viewBox = svgStr.match(/viewBox="([^'"]*)"/);
    const sizeStr = viewBox ? viewBox[1] : "0, 0";
    const sizes = sizeStr.match(/\d+/g);
    height = +sizes.pop();
    width = +sizes.pop();
  }

  return {
    width,
    height
  };
}; // svg高度自适应宽度
// https://stackoverflow.com/questions/19484707/how-can-i-make-an-svg-scale-with-its-parent-container

const fixSvgScale = svgStr => {
  return svgStr.replace(/width="[^'"]+"/, `width="100%"`).replace(/height="[^"']+"/, "");
}; // 添加使svg保持缩放比的字段

function fixSvgRatio({
  svgStr,
  width,
  height
}) {
  svgStr = svgStr.slice(4);
  let prefix = "<svg ";

  if (!width || !height) {
    const sizeInfo = getSizeFromSvg(svgStr);
    width = sizeInfo.width;
    height = sizeInfo.height;
  }

  if (!svgStr.includes("xMidYMid slice")) {
    prefix += 'preserveAspectRatio="xMidYMid slice" ';
  }

  if (!svgStr.includes("viewBox")) {
    prefix += `viewBox="0, 0, ${width}, ${height}"`;
  } else {
    svgStr = svgStr.replace(/viewBox="[^"]+"/, `viewBox="0, 0, ${width}, ${height}"`);
  }

  return prefix + fixSvgScale(svgStr);
}
/**
 * 修复svg内容二次编辑后后宽高比改变
 * 如果调整过宽度，则按新宽度展示
 * @param {number} displayWidth 展示的宽度
 * @param {string} newSvgStr 新svg字符串
 * @param {boolean} hadResize 是否调整过宽度
 * @param {boolean} formatted 返回格式化好的样式对象
 */

function getSvgSizeAfterEdit({
  displayWidth,
  newSvgStr,
  hadResize,
  formatted = false
}) {
  const {
    width: newWidth,
    height: newHeight
  } = getSizeFromSvg(newSvgStr);

  if (!hadResize) {
    return undefined;
  }

  const scale = displayWidth / newWidth;
  let width = displayWidth;
  let height = newHeight * scale;

  if (formatted) {
    width = `${width}px`;
    height = `${height}px`;
  }

  return {
    width,
    height
  };
} //copy自 citadel-web, 线上附件调整isNewContent

function getDynamicSrc(url) {
  const href = window.location.href;
  const isNewContent = ['production'].includes(config.env) ? href.includes('create') : config.env;

  if (url.includes('api/file/cdn')) {
    url += `&isNewContent=${isNewContent}`;
    if (href.includes('template') && !href.includes('create') && !url.includes('template')) url += '&template=1';
    return url;
  }

  const urlReg = /^https:\/\/km.*\.meituan\.net.*contentId=(\d+)&attachmentId=(\d+).*&contentType=(\d)/i;
  const [_, contentId, attachmentId, contentType] = urlReg.exec(url);
  return `${window.location.origin}/api/file/cdn/${contentId}/${attachmentId}?contentType=${contentType}&isNewContent=${isNewContent}`;
} // 复制到粘贴板

const mobileCopyToClipboard = (content, successStr, event) => {
  const clip = new ClipBoard('.btn', {
    text: () => content
  });
  clip.on('success', e => {
    Toast.info(successStr || '复制成功');
    e.clearSelection();
  });
  clip.on('error', err => {
    Toast.info('当前浏览器不支持快捷复制,请手动复制');
    console.error(err);
  });
  clip.onClick(event);
  clip.destroy();
}; // 复制到粘贴板

const copyToClipboard = (content, successStr) => {
  const inputEle = document.createElement('input');
  inputEle.value = content;
  document.body.appendChild(inputEle);
  inputEle.select();

  try {
    document.execCommand('Copy');
    Toastr.success(successStr || '复制成功');
  } catch (err) {
    Toastr.success('当前浏览器不支持快捷复制,请手动复制');
    console.error(err);
  }

  document.body.removeChild(inputEle);
};

/* eslint-disable camelcase */
function handleBackspaceCreator(nodeTypeName, titleNodeTypeName, contentNodeTypeName) {
  return function handleBackspace(state, dispatch) {
    const {
      tr,
      schema,
      selection
    } = state;
    const {
      paragraph
    } = schema.nodes;
    const note = schema.nodes[nodeTypeName];
    const note_title = schema.nodes[titleNodeTypeName];
    const note_content = schema.nodes[contentNodeTypeName];
    const {
      $cursor,
      $from,
      $to,
      from,
      to
    } = selection;

    const assert1 = node => node.type === note;

    const assert2 = node => node.type === note_title;

    const assert3 = node => node.type === note_content; // 阻止删除标题和内容


    if ($cursor) {
      const parentType = $cursor.parent.type;
      const plus = parentType.isInline ? 2 : 1;
      const contentNode = $cursor.node(-plus);

      if (!$cursor.nodeBefore && (parentType === note_title || parentType === note_content || contentNode && contentNode.type === note_content && contentNode.firstChild === $cursor.node(-plus + 1))) {
        return true;
      }
    } else {
      const fromNoteNode = findParentNode($from.path, assert1);
      const toNoteNode = findParentNode($to.path, assert1); // 跨不同信息宏删除

      if (fromNoteNode && toNoteNode && fromNoteNode.pos !== toNoteNode.pos || !fromNoteNode && toNoteNode || fromNoteNode && !toNoteNode) {
        let newFrom = from;
        let newTo = to;
        if (fromNoteNode) newFrom = fromNoteNode.pos;
        if (toNoteNode) newTo = toNoteNode.pos + toNoteNode.node.nodeSize;
        dispatch(tr.delete(newFrom, newTo));
        return true;
      } else if (fromNoteNode) {
        // 同一个信息宏内跨标题和内容删除
        const noteNode = fromNoteNode.node;
        const fromNoteTitleNode = findParentNode($from.path, assert2);
        const toNoteContentNode = findParentNode($to.path, assert3);

        if (fromNoteTitleNode && toNoteContentNode && noteNode.firstChild === fromNoteTitleNode.node && noteNode.lastChild === toNoteContentNode.node) {
          const end = fromNoteTitleNode.pos + fromNoteTitleNode.node.nodeSize - 1;
          const dis = end - from;
          tr.delete(from, end);
          tr.delete(toNoteContentNode.pos - dis, to - dis);
          tr.setSelection(TextSelection.create(tr.doc, from));
          dispatch(tr);
          return true;
        } // 全选内容删除


        if (toNoteContentNode && from - toNoteContentNode.pos < 3 && toNoteContentNode.pos + toNoteContentNode.node.nodeSize - to < 3) {
          const start = toNoteContentNode.pos + 1;
          const end = toNoteContentNode.pos + toNoteContentNode.node.nodeSize - 1;
          tr.replaceWith(start, end, paragraph.create());
          tr.setSelection(TextSelection.create(tr.doc, from));
          dispatch(tr);
          return true;
        }
      }
    }

    return false;
  };
}
function handleDeleteCreator(nodeTypeName, titleNodeTypeName, contentNodeTypeName) {
  return function handleDelete(state, dispatch) {
    const {
      tr,
      schema,
      selection
    } = state;
    const note = schema.nodes[nodeTypeName];
    const note_title = schema.nodes[titleNodeTypeName];
    const note_content = schema.nodes[contentNodeTypeName];
    const {
      $cursor,
      $from,
      $to,
      from,
      to
    } = selection;

    const assert1 = node => node.type === note;

    const assert2 = node => node.type === note_title;

    const assert3 = node => node.type === note_content; // 阻止删除标题和内容


    if ($cursor) {
      const parentType = $cursor.parent.type;
      const plus = parentType.isInline ? 2 : 1;

      if (!$cursor.nodeAfter && ($cursor.parent.type === note_title || $cursor.node(-plus).type === note_content)) {
        return true;
      }
    } else {
      const fromNoteNode = findParentNode($from.path, assert1);
      const toNoteNode = findParentNode($to.path, assert1); // 跨不同信息宏删除

      if (fromNoteNode && toNoteNode && fromNoteNode.pos !== toNoteNode.pos || !fromNoteNode && toNoteNode || fromNoteNode && !toNoteNode) {
        let newFrom = from;
        let newTo = to;
        if (fromNoteNode) newFrom = fromNoteNode.pos;
        if (toNoteNode) newTo = toNoteNode.pos + toNoteNode.node.nodeSize;
        dispatch(tr.delete(newFrom, newTo));
        return true;
      } // 同一个信息宏内跨标题和内容删除


      const fromNoteTitleNode = findParentNode($from.path, assert2);
      const toNoteContentNode = findParentNode($to.path, assert3);

      if (fromNoteTitleNode && toNoteContentNode) {
        const end = fromNoteTitleNode.pos + fromNoteTitleNode.node.nodeSize - 1;
        const dis = end - from;
        tr.delete(from, end);
        tr.delete(toNoteContentNode.pos - dis, to - dis);
        tr.setSelection(TextSelection.create(tr.doc, from));
        dispatch(tr);
        return true;
      } // 全选内容删除


      if (toNoteContentNode && toNoteContentNode.pos + 1 === from && toNoteContentNode.pos + toNoteContentNode.node.nodeSize - 1 === to) {
        tr.delete(from + 1, to - 1);
        tr.setSelection(TextSelection.create(tr.doc, from));
        dispatch(tr);
        return true;
      }
    }

    return false;
  };
}
function handleTabCreator(nodeTypeName, titleNodeTypeName, contentNodeTypeName) {
  return function handleTab(state, dispatch) {
    const {
      tr,
      schema,
      selection
    } = state;
    const note_title = schema.nodes[titleNodeTypeName];
    const {
      $from
    } = selection;

    const assert = node => node.type === note_title;

    const foundNode = findParentNode($from.path, assert);

    if (foundNode) {
      tr.setSelection(TextSelection.create(tr.doc, foundNode.pos + foundNode.node.nodeSize + 2));
      dispatch(tr);
      return true;
    }

    return false;
  };
}
function handleEnterCreator(nodeTypeName, titleNodeTypeName, contentNodeTypeName) {
  return function handleEnter(state, dispatch, view) {
    const {
      tr,
      schema,
      selection
    } = state;
    const {
      $from
    } = selection;
    const note_title = schema.nodes[titleNodeTypeName];
    const foundNode = findParentNode($from.path, node => node.type === note_title);

    if (foundNode) {
      const {
        node,
        pos
      } = foundNode;
      const newSelection = TextSelection.create(tr.doc, pos + node.nodeSize + 1);
      dispatch(tr.setSelection(newSelection));
      view.focus();
      return true;
    }

    return false;
  };
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var defineProperty = _defineProperty;

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      defineProperty(target, key, source[key]);
    });
  }

  return target;
}

var objectSpread = _objectSpread;

const uploadHelper = config => {
  let preUpload = () => '';

  let onProgress = () => null;

  let finishUpload = () => {};

  const {
    suffixes,
    limitSize,
    onSuffixError,
    onSizeError
  } = config;

  const setter = files => {
    for (const file of files) {
      if (suffixes && !checkSuffix(file.name, suffixes)) {
        if (onSuffixError) onSuffixError(file.name);
        continue;
      }

      if (limitSize && file.size / (1024 * 1024) > limitSize) {
        if (onSizeError) onSizeError(file.size);
        continue;
      }

      doUpload(file, config, {
        preUpload,
        onProgress,
        finishUpload
      });
    }
  };

  const getter = (pre, on, finish) => {
    preUpload = pre;
    onProgress = on;
    finishUpload = finish;
    return setter;
  };

  return [getter, setter];
};

const checkSuffix = (name, suffixes) => {
  const temp = /\.[^.]+$/.exec(name);

  if (temp) {
    const suffix = temp[0].slice(1).toLowerCase();
    return suffixes.findIndex(s => suffix === s) > -1;
  }

  return false;
};

const doUpload = (file, config, hooks) => {
  const {
    uploadUrl,
    onUploadError
  } = config;
  const {
    preUpload,
    onProgress,
    finishUpload
  } = hooks;
  const isImage = file.type.indexOf('image') > -1;
  const key = preUpload({
    file,
    isImage
  });
  upload({
    uploadUrl,
    onProgress: e => onProgress({
      file,
      isImage,
      key
    }, e),
    addition: {}
  }, file).then(res => {
    finishUpload(objectSpread({
      key,
      res,
      file,
      isImage,
      size: file.size
    }, config));
  }).catch(err => onUploadError && onUploadError(err, key));
};

function upload(conf = {}, file) {
  const config = Object.assign({}, defaultConf, conf);
  const {
    uploadUrl,
    addition,
    onProgress
  } = config;
  const formData = fileFormBuilder(addition, file);
  return uploadFile(uploadUrl, {
    body: formData
  }, onProgress);
}

const defaultConf = {
  uploadUrl: '',
  onProgress: () => {}
};

function fileFormBuilder(data = {}, file) {
  const formData = new FormData();
  const name = data.name || 'file';

  if (data.filename) {
    formData.append(name, file, data.filename);
  } else {
    formData.append(name, file);
  }

  Object.keys(data).forEach(key => {
    if (key !== 'name' && key !== 'filename') {
      formData.append(key, data[key]);
    }
  });
  return formData;
}

function uploadFile(url, opts = {}, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('post', url);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    for (const k in opts.headers || {}) {
      xhr.setRequestHeader(k, opts.headers[k]);
    }

    xhr.onload = () => {
      if (xhr.status === 401 || xhr.message === 'auth failed') {
        reject(xhr);
      } else {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (error) {
          resolve(xhr.responseText);
        }
      }
    };

    xhr.onerror = () => {
      reject(xhr);
    };

    xhr.ontimeout = () => {
      reject(xhr);
    };

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = onProgress;
    }

    xhr.send(opts.body);
  });
}

class PopoverStore {
  constructor() {
    this.view = null;
    this.modules = null;
    this.$scroll = null;
    this.popover = null;
    this.domNode = null;
    this.isShow = false;
    this.scrollTop = 0;

    this.handleScroll = () => {
      if (!this.isShow) return;
      const scrollTop = this.$scroll.scrollTop;
      this.popover.style.top = `${parseFloat(this.popover.style.top) + this.scrollTop - scrollTop}px`;
      this.scrollTop = scrollTop;
    };
  }

  init(moduleManager) {
    this.view = moduleManager.view;
    this.$scroll = moduleManager.$scroll;
    this.modules = moduleManager.modules;

    if (this.$scroll.tagName === 'HTML') {
      window.addEventListener('scroll', this.handleScroll);
    } else {
      this.$scroll.addEventListener('scroll', this.handleScroll);
    }
  }

  onCreatePopover(popover) {
    this.popover = popover;
  }

  onRemovePopover() {
    this.hidePopover();

    if (this.$scroll.tagName === 'HTML') {
      window.removeEventListener('scroll', this.handleScroll);
    } else {
      this.$scroll.removeEventListener('scroll', this.handleScroll);
    }
  }

  reShowPopover() {
    if (this.domNode) this.showPopover(this.domNode);
  }

  showPopover(domNode) {
    if (this.popover) {
      this.popover.style.display = 'block';
      this.domNode = domNode;
      this.isShow = true;
      this.scrollTop = this.$scroll.$scrollTop;
    }
  }

  hidePopover() {
    if (this.popover) {
      this.popover.style.display = 'none';
      this.isShow = false;
    }
  }

}

const createPlaceholder = (view, plugin, key, pos) => {
  const {
    state,
    dispatch
  } = view;
  const placeholderPlugin = state.config.pluginsByKey[plugin];
  dispatch(state.tr.setMeta(placeholderPlugin, {
    add: {
      key,
      pos
    }
  }).insertText("\u200B", pos) // 为了能够在placeholder后面输入文字
  .scrollIntoView());
  view.focus();
};
const deletePlaceholder = (view, plugin, key) => {
  const {
    state,
    dispatch
  } = view;
  const placeholderPlugin = state.config.pluginsByKey[plugin];

  try {
    const transaction = state.tr.setMeta(placeholderPlugin, {
      remove: {
        key
      }
    });
    dispatch(transaction);
    view.focus();
  } catch (err) {
    console.error("placeholder maybe deleted", err);
  }
};
const findPlaceholder = (view, plugin, key) => {
  const {
    state
  } = view;
  const placeholderPlugin = state.config.pluginsByKey[plugin];
  const found = placeholderPlugin.getState(state).find(null, null, spec => spec.key === key);

  if (found.length) {
    return {
      pos: found[0].from,
      node: found[0].type.toDOM
    };
  }

  return null;
};

const createShadowDOM = ({
  dom,
  content
}) => {
  if (!document.body.attachShadow) {
    dom.innerHTML = content;
    return;
  }

  const shadowWrapper = document.createElement("div");
  shadowWrapper.setAttribute("class", SHADOWDOM_CLASS);
  const shadowRoot = shadowWrapper.attachShadow({
    mode: "open"
  });
  shadowRoot.innerHTML = "<style>svg { width: 100%; height: 100%; } svg * { user-select: none }</style>";
  shadowRoot.innerHTML += content;
  dom.appendChild(shadowWrapper);
};
const getElementFromDOM = ({
  selector,
  dom
}) => {
  const shawdowWrapper = dom.querySelector(`.${SHADOWDOM_CLASS}`);

  if (shawdowWrapper && shawdowWrapper.shadowRoot) {
    return shawdowWrapper.shadowRoot.querySelector(selector);
  }

  if (selector === "div") {
    return dom;
  }

  return dom.querySelector(selector);
};

/**
 * 以默认参数发送请求
 *
 * @param url
 * @param options
 * @param isRedirect
 * @param timeout
 * @returns {Promise.<TResult>}
 */
const DEFAULT_OPTIONS = {
  method: "GET",
  credentials: "include",
  headers: {
    "Cache-Control": "no-cache",
    "X-Requested-With": "XMLHttpRequest"
  }
};
const DEFAULT_TIMEOUT = 1e4;
function advancedFetch(fetchPromise, timeout) {
  let abortFn = () => {};

  const abortPromise = new Promise((resolve, reject) => {
    abortFn = () => {
      reject(new Error("abort promise"));
    };
  });
  const abortablePromise = Promise.race([fetchPromise, abortPromise]);
  const timer = setTimeout(() => {
    abortFn();
  }, timeout || DEFAULT_TIMEOUT);
  return abortablePromise.then(data => {
    clearTimeout(timer);
    return data;
  });
}

const text2JSON = resText => {
  let resJson;

  try {
    resJson = JSON.parse(resText);

    if (resJson.status !== 0) {
      const {
        errorCode,
        message
      } = resJson.data;
      return Promise.reject(console.error(errorCode, message));
    }
  } catch (err) {
    return Promise.resolve(resText);
  }

  if (resJson.data !== undefined) {
    return Promise.resolve(resJson.data);
  }

  return Promise.resolve(resJson);
};

function transform(query) {
  return Object.keys(query).map(key => `${key}=${query[key]}`);
}

const send = (url, options, isRedirect = true, timeout) => {
  let fetchUrl = url;

  if (options && options.query) {
    fetchUrl += `?${transform(options.query).join('&')}`;
  }

  const fetchOptions = Object.assign({}, DEFAULT_OPTIONS, options);
  return advancedFetch(fetch(fetchUrl, fetchOptions), timeout).then(response => {
    if (response.ok) {
      return response.text();
    }

    return response.text().then(text2JSON).then(resJson => Promise.reject(new Error(`status: ${response.status}, message: ${resJson.message} }`)));
  }).then(text2JSON).catch(error => {
    if (error.message === "auth failed") {
      if (isRedirect) {
        location.reload();
      }
    }

    return Promise.reject(error);
  });
};

function createToolbar(onMouseDown) {
  const $imgEditor = document.createElement("span");
  $imgEditor.className = RESIZER_CLASS;
  $imgEditor.innerHTML = `
      <span class="info info-rb"></span>
      <span class="resizer resizer-lt"></span>
      <span class="resizer resizer-lb"></span>
      <span class="resizer resizer-rt"></span>
      <span class="resizer resizer-rb"></span>
    `;
  $imgEditor.addEventListener("mousedown", onMouseDown, false);
  return $imgEditor;
} // 拖拽时计算图片宽度
// isAdd: 左侧两个拖动，宽度需要相减，右侧两个则相反

function calculateWidth(width, diff, isAdd, max, min) {
  const result = isAdd ? width + diff : width - diff;

  if (result <= min) {
    return min;
  } else if (result >= max) {
    return max;
  }

  return result;
}
function calculateHeight(height, diffY, isAddY, max, min) {
  const result = isAddY ? height + diffY : height - diffY;

  if (result <= min) {
    return min;
  } else if (result >= max) {
    return max;
  }

  return result;
}
function getContentDisplaySize({
  type,
  dom,
  resizer,
  clientX,
  clientY
}) {
  const imgNode = getElementFromDOM({
    dom,
    selector: type
  });
  const {
    isAdd,
    isAddY,
    startX,
    startY,
    originalWidth,
    originalHeight,
    maxWidth
  } = resizer;
  const diff = clientX - startX;
  const diffY = clientY - startY;
  let naturalHeight = 0;
  let naturalWidth = 0;
  let width = 0;
  let height = 0;

  switch (type) {
    case "img":
      naturalWidth = imgNode.naturalWidth;
      naturalHeight = imgNode.naturalHeight;
      break;

    case "svg":
      {
        naturalWidth = imgNode.viewBox.baseVal.width;
        naturalHeight = imgNode.viewBox.baseVal.height;
        break;
      }

    case "div":
      {
        naturalWidth = imgNode.offsetWidth;
        naturalHeight = imgNode.offsetHeight;
        break;
      }
  }

  let minWidth = naturalWidth;

  if (naturalWidth < 5 || naturalWidth > 20) {
    minWidth = IMAGE_MIN_WIDTH;
  }

  width = calculateWidth(originalWidth, diff, isAdd, maxWidth, minWidth);

  if (type !== "div") {
    if (naturalHeight) {
      height = width / naturalWidth * naturalHeight;
    }
  } else {
    if (naturalHeight) {
      height = calculateHeight(originalHeight, diffY, isAddY, 600, 150);
      width = width < 320 ? 320 : width;
    }
  }

  return {
    width,
    height,
    imgNode
  };
}

var css$1 = ".ct-resizer{display:block;position:absolute;left:0;right:0;top:0;bottom:0;z-index:11;outline:1px dashed #aaa;pointer-events:none}.ct-resizer .resizer{position:absolute;width:24px;height:24px;pointer-events:auto}.ct-resizer .resizer:after{box-sizing:border-box;content:\" \";position:absolute;display:block;top:5px;left:5px;width:14px;height:14px;border-radius:7px;border:2px solid #fff;box-shadow:0 1px 2px 0;background-color:#333}.ct-resizer .info{position:absolute;padding:5px;font-size:12px;color:#fff;background:rgba(0,0,0,.7)}.ct-resizer .info-lt{left:8px;top:8px}.ct-resizer .info-lb{left:8px;bottom:8px}.ct-resizer .info-rt{right:8px;top:8px}.ct-resizer .info-rb{right:8px;bottom:8px}.ct-resizer .resizer-lt{left:-12px;top:-12px;cursor:nwse-resize}.ct-resizer .resizer-lb{left:-12px;bottom:-12px;cursor:nesw-resize}.ct-resizer .resizer-rt{right:-12px;top:-12px;cursor:nesw-resize}.ct-resizer .resizer-rb{right:-12px;bottom:-12px;cursor:nwse-resize}";
styleInject(css$1);

/**
 * @description
 *  统一缩放放大处理
 *  需提供一个 updateAttrs(attrs) 的函数，用于更新宽高
 * @method ctShowResizer ({ node? = { dom }, type }) => {}
 * @method ctHideResizer () => {}
 * @method ctToggleDisplayOfImageInfo 更新缩放框右下角数值
 */
const RESIZING = "resizing";
function resizer(constructor) {
  constructor.prototype.ctShowResizer = showResizer;
  constructor.prototype.ctHideResizer = hideResizer;
  constructor.prototype.ctToggleDisplayOfImageInfo = toggleDisplayOfImageInfo;
  constructor.prototype.ctResizeType = "img";
  constructor.prototype.resizer = null;
  constructor.prototype.node = null;
  constructor.prototype.$imageInfo = null;
  constructor.prototype.$resizer = null;
  constructor.prototype.startMoveCallback = null;
  constructor.prototype.endMoveCallback = null;

  constructor.prototype.setStartMoveCallback = function (cb) {
    this.startMoveCallback = cb;
  };

  constructor.prototype.setEndMoveCallback = function (cb) {
    this.endMoveCallback = cb;
  };

  return constructor;
}
let onMouseDown;
let onMouseMove;
let onMouseUp;

function resizerOnMouseDown(e) {
  if (!e.target) {
    return;
  }

  const classList = e.target.classList;

  if (!classList.contains("resizer")) {
    return;
  }

  if (!this.$imageInfo) {
    // this.hideHelper()
    return;
  }

  e.preventDefault();
  const isLeftTop = classList.contains("resizer-lt");
  const isLeftBottom = classList.contains("resizer-lb");
  const isRightTop = classList.contains("resizer-rt");
  const isRightBottom = classList.contains("resizer-rb");
  const isAdd = isRightTop || isRightBottom;
  const isAddY = isLeftBottom || isRightBottom;

  if (isLeftTop) {
    this.$imageInfo.className = "info info-lt";
  } else if (isLeftBottom) {
    this.$imageInfo.className = "info info-lb";
  } else if (isRightTop) {
    this.$imageInfo.className = "info info-rt";
  } else if (isRightBottom) {
    this.$imageInfo.className = "info info-rb";
  } // 父容器宽度 - 图片容器外层宽度


  const blockNode = findParentBlockNode(this.node.dom);

  if (!blockNode) {
    return;
  }

  const parentTagName = blockNode.parentNode.tagName.toLowerCase();
  let maxWidth = blockNode.clientWidth - IMAGE_OFFSET;

  if ((parentTagName === "td" || parentTagName === "th") && blockNode.closest("table").classList.contains("responsive")) {
    maxWidth = 1920;
  }

  this.resizer = {
    maxWidth,
    isAdd,
    isAddY,
    isResizing: false,
    startX: e.clientX,
    startY: e.clientY,
    originalWidth: this.node.dom.clientWidth,
    originalHeight: this.node.dom.clientHeight
  };
  setResizerState();
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
}

let resizerOnMouseMove = function (e) {
  if (!this.resizer) {
    return;
  }

  if (this.node.dom.querySelector(".ct-editor-loading")) {
    return;
  }

  const {
    width,
    height
  } = getContentDisplaySize({
    type: this.ctResizeType,
    dom: this.node.dom,
    resizer: this.resizer,
    clientX: e.clientX,
    clientY: e.clientY
  });
  this.resizer.width = width;

  if (height) {
    this.resizer.height = height;
  }

  this.node.dom.style.width = `${this.resizer.width}px`;

  if (height) {
    this.node.dom.style.height = `${this.resizer.height}px`;
  }

  if (this.startMoveCallback) {
    this.startMoveCallback(height, width);
  }

  this.ctToggleDisplayOfImageInfo();
  this.resizer.isResizing = true;
};

resizerOnMouseMove = throttle(resizerOnMouseMove, 16);

function resizerOnMouseUp() {
  if (!this.resizer) {
    return;
  }

  if (!this.resizer.isResizing) {
    this.resizer = null;
    return;
  }

  const {
    isResizing,
    width
  } = this.resizer;

  if (!isResizing || !width) {
    return;
  }

  this.resizer.isResizing = false;
  const imgNode = getElementFromDOM({
    dom: this.node.dom,
    selector: this.ctResizeType
  });

  if (imgNode) {
    try {
      this.updateAttrs({
        width: imgNode.clientWidth,
        height: imgNode.clientHeight,
        hadResize: true
      });
    } catch (err) {
      console.error(err);
    }
  }

  dropResizerState();

  if (this.endMoveCallback) {
    this.endMoveCallback();
  }

  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
}

function toggleDisplayOfImageInfo() {
  const spanDOM = this.node.dom;
  const styleWidth = parseInt(spanDOM.style.width, 10);
  let width = spanDOM.clientWidth;
  let height = spanDOM.clientHeight;

  if (styleWidth && width !== styleWidth) {
    height = parseInt(`${styleWidth / width * height}`, 10);
    width = styleWidth;
  }

  const textContent = `${width} × ${height}`;

  if (spanDOM.clientWidth < textContent.length * 8 + 10 || spanDOM.clientHeight < IMAGE_INFO_DISPLAY_MIN_HEIGHT_SIZE) {
    this.$imageInfo.style.display = "none";
  } else {
    this.$imageInfo.style.display = "block";
    this.$imageInfo.textContent = textContent;
  }
}

function hideResizer() {
  if (this.$resizer) {
    this.$resizer.remove();
    this.$resizer = null;
    this.$imageInfo = null;
    this.resizer = null;
  }
}

function showResizer(config) {
  if (config) {
    if (config.node) {
      this.node = config.node;
    }

    this.ctResizeType = config.type;
  }

  onMouseDown = resizerOnMouseDown.bind(this);
  onMouseMove = resizerOnMouseMove.bind(this);
  onMouseUp = resizerOnMouseUp.bind(this);
  this.$resizer = createToolbar(onMouseDown);
  this.$imageInfo = this.$resizer.firstElementChild;
  this.node.dom.appendChild(this.$resizer);
  this.ctToggleDisplayOfImageInfo();
} // 缩放时为了吸顶而设置的popover的高度会阻碍到编辑器的innerHeight的改变


function setResizerState() {
  const popovers = document.getElementsByClassName(POPOVER_CLASS);
  window.ctImgResizing = true; // 全局缩放状态

  Array.from(popovers).forEach(node => {
    if (node.childNodes.length > 0) {
      node.classList.add(RESIZING);
    }
  });
}

function dropResizerState() {
  const popovers = document.getElementsByClassName(POPOVER_CLASS);
  window.ctImgResizing = false; // 全局缩放状态

  Array.from(popovers).forEach(node => {
    if (node.classList.contains(RESIZING)) {
      node.classList.remove(RESIZING);
    }
  });
}

var css$2 = ".ct-toolbox-popover{display:none;position:absolute;width:-webkit-fit-content;width:-moz-fit-content;width:fit-content;height:-webkit-fit-content;height:-moz-fit-content;height:fit-content;pointer-events:none;background:transparent;z-index:98;transition:opacity .2s ease}.ct-toolbox-popover.resizing{height:-webkit-fit-content!important;height:-moz-fit-content!important;height:fit-content!important}.ct-toolbox-popover .ct-toolbox{position:-webkit-sticky;position:sticky;display:flex;top:0;flex-direction:row;align-items:center;background:#fff;font-size:12px;box-shadow:0 4px 16px 0 rgba(106,93,73,.2),0 8px 20px 0 rgba(106,93,73,.1);border-radius:1px;pointer-events:all}.ct-toolbox-popover .ct-toolbox.in-table{top:30px}.ct-toolbox-popover .ct-toolbox.nosticky{position:relative}.ct-toolbox-popover .ct-toolbox.nosticky.in-table{top:0}";
styleInject(css$2);

const getDOMOffset = ({
  offsetParent,
  dom,
  toolboxHeight,
  bottomFirst = false,
  popoverWidth
}) => {
  let top = 0;
  let left = 0;
  let right = "auto";
  const {
    left: domLeft,
    top: domTop,
    bottom: domBottom
  } = dom.getBoundingClientRect();
  const {
    left: parentLeft,
    top: parentTop,
    right: parentRight
  } = offsetParent.getBoundingClientRect();
  const avHeight = window.innerHeight;
  top = domTop - parentTop; // dom顶部距到视口顶部距离

  left = domLeft - parentLeft; // dom距离页面左边距

  if (bottomFirst && avHeight - domBottom - 50 > toolboxHeight) {
    // 顶部高度大于toolboxheight
    top = domBottom - parentTop + 2; // dom底部距离页面到视口顶部距离
  } else {
    top = top - toolboxHeight - 2; // 显示在上方
  } // 右边剩余空间


  const rightWidth = parentRight - domLeft;

  if (rightWidth < popoverWidth) {
    left = "auto";
    right = 0;
  }

  if (left < 0) {
    left = 0;
    right = "auto";
  }

  return {
    left,
    top,
    right
  };
};
const setPopoverStyle = ({
  $popover,
  dom,
  customStyle,
  offsetParent,
  toolboxHeight,
  bottomFirst,
  popoverWidth
}) => {
  $popover.style.display = "block";

  if (dom) {
    const height = dom.offsetHeight + toolboxHeight;

    if (!offsetParent || !$popover) {
      return;
    }

    const style = getDOMOffset({
      offsetParent,
      dom,
      toolboxHeight,
      bottomFirst,
      popoverWidth
    });
    Object.entries(style).forEach(([key, value]) => {
      if (/[0-9]+/.test(value)) {
        $popover.style[key] = `${value}px`;
      } else {
        $popover.style[key] = `${value}`;
      }
    });
    $popover.style.height = `${height}px`;
  } else if (customStyle) {
    Object.keys(customStyle).forEach(key => {
      $popover.style[key] = customStyle[key];
    });
  }
};
const emptyDOM = renderDOM => {
  if (renderDOM.firstElementChild) {
    render(null, renderDOM, renderDOM.firstElementChild);
  }
};

const SHOW = "show";
const HIDE = "hide";
const TOOLBOX_HEIGHT = 34;
class Popover {
  // 工具栏高度
  // 工具栏高度
  // 表格节点
  constructor({
    view,
    store,
    className,
    height,
    autoControl = true,
    bottomFirst = false,
    sticky = true,
    getToolboxHeightFn
  }) {
    this.toolboxHeight = void 0;
    this.getToolboxHeightFn = void 0;
    this.offsetParent = void 0;
    this.offsetParentRect = void 0;
    this.$popover = void 0;
    this.store = void 0;
    this.view = void 0;
    this.pluginName = void 0;
    this.isAutoControl = void 0;
    this.isBottomFirst = void 0;
    this.isSticky = void 0;
    this.tableWrapper = void 0;
    this.popoverWidth = void 0;
    this.tableOffset = void 0;
    this.popoverState = HIDE;

    this.setPopoverState = state => {
      this.popoverState = state;
    };

    this.checkTable = dom => {
      if (!dom) {
        return;
      }

      this.tableWrapper = dom.closest(".ct-table-wrapper");
    };

    this.fixTableScroll = () => {
      if (!this.isAutoControl) {
        return;
      }

      if (this.$popover.firstElementChild) {
        if (this.tableWrapper) {
          this.$popover.firstElementChild.classList.add("in-table");
          this.tableWrapper.addEventListener("scroll", this.updatePopover);
        } else {
          this.tableOffset = null;
          this.$popover.firstElementChild.classList.remove("in-table");
        }
      }
    };

    this.updatePopover = () => {
      const node = objectSpread({}, this.store.node);

      this.store.hidePopover();
      this.store.showPopover(node);
    };

    this.toolboxHeight = height || TOOLBOX_HEIGHT;
    this.getToolboxHeightFn = getToolboxHeightFn;
    this.isAutoControl = autoControl;
    this.isBottomFirst = bottomFirst;
    this.isSticky = sticky;
    this.updatePopover = throttle(this.updatePopover, 16);
    this.tableOffset = null;
    this.store = store;
    this.view = view;
    this.$popover = document.createElement("div");
    this.$popover.className = `ct-toolbox-popover ${className || ""}`;
    this.store.ctPopover = this;

    if (view.dom.parentElement) {
      view.dom.parentElement.appendChild(this.$popover);
    }
  }

  show({
    dom,
    content,
    customStyle,
    forceUpdate = false
  }) {
    if (this.popoverState === SHOW && !forceUpdate) {
      return;
    }

    if (this.popoverState === HIDE) {
      const offsetParent = this.view.dom.parentElement;
      this.checkTable(dom);
      let toolboxHeight = this.toolboxHeight;

      if (this.getToolboxHeightFn) {
        toolboxHeight = this.getToolboxHeightFn();
      }

      setPopoverStyle({
        $popover: this.$popover,
        dom,
        customStyle,
        offsetParent,
        toolboxHeight,
        bottomFirst: this.isBottomFirst,
        popoverWidth: this.popoverWidth
      });
    }

    if (forceUpdate) {
      emptyDOM(this.$popover);
    }

    render(content, this.$popover);

    if (this.$popover.firstElementChild) {
      const $child = this.$popover.firstElementChild;
      $child.classList.add("ct-toolbox");

      if (!this.isSticky) {
        $child.classList.add("nosticky");
      }

      this.fixTableScroll();
      this.setPopoverState(SHOW);
    }

    if (!this.popoverWidth) {
      this.updatePopover();
    }
  }

  hide() {
    if (this.popoverState === HIDE) {
      return;
    }

    this.popoverWidth = this.$popover.offsetWidth;
    emptyDOM(this.$popover);

    if (this.isAutoControl && this.tableWrapper) {
      this.tableWrapper.removeEventListener("scroll", this.updatePopover);
    }

    this.$popover.setAttribute("style", "");
    this.$popover.style.display = "none";
    this.setPopoverState(HIDE);
  }

  destroy() {
    this.$popover.remove();
  } // popover具体显隐在update做控制


  update(view) {
    const selection = view.state.selection; // const { state: newState } = view;

    const selNode = selection.node;

    if (this.store.handleViewUpdate) {
      this.store.handleViewUpdate({
        view,
        selNode,
        selection
      });
    } else if (this.isAutoControl && this.store.pluginName) {
      // 自动控制popover显隐
      try {
        // if (newState.tableColumnResizing$.dragging || !newState.selection.eq(prevState.selection)) {
        const pluginName = this.store.pluginName.match(/([a-z|A-Z]+)/)[0].toLowerCase();
        let pos = null;
        let attrs = {};
        let dom = null; // 直接选中的情况

        if (selection.node && selection.node.type.name.toLowerCase() === pluginName) {
          const {
            $from
          } = selection;
          pos = $from.pos;
          const {
            nodeAfter
          } = $from;
          attrs = nodeAfter.attrs;
        } // 嵌套的情况


        if (pos === null) {
          const findResult = findParentNode(selection.$anchor.path, item => item.type && item.type.name === pluginName);

          if (findResult) {
            pos = findResult.pos;
            attrs = findResult.node.attrs;
          }
        }

        dom = view.nodeDOM(pos);

        if (dom && dom.classList && !dom.classList.contains("failed")) {
          const {
            content
          } = attrs;
          this.store.hidePopover();
          this.store.showPopover({
            dom,
            pos,
            data: content,
            attrs,
            view
          });
        } else if (this.store.hidePopover) {
          this.store.hidePopover();
        } // }

      } catch (err) {
        console.error("showPopover failed", err);
      }
    }
  }

}

/**
 * 计算规则，需要按顺序满足下面两项
 * 1. 高度不能高于 imageMaxHeight
 * 2. 宽度不能大于容器宽度
 */

function calculateSize(originWidth, originHeight, containerWidth, imageMaxHeight = IMAGE_MAX_HEIGHT) {
  const result = {
    newOriginHeight: 0,
    newOriginWidth: 0,
    height: 0,
    width: 0
  };

  if (originHeight < imageMaxHeight) {
    result.newOriginWidth = originWidth;
    result.newOriginHeight = originHeight;

    if (originWidth > containerWidth) {
      result.width = containerWidth;
      result.height = +(originHeight * containerWidth / originWidth).toFixed(2);
    } else {
      result.width = originWidth;
      result.height = originHeight;
    }
  } else {
    result.width = +(originWidth * imageMaxHeight / originHeight).toFixed(2);
    result.height = imageMaxHeight;
    result.newOriginWidth = result.width;
    result.newOriginHeight = imageMaxHeight;

    if (result.width > containerWidth) {
      result.width = containerWidth; // result.height = +(imageMaxHeight * containerWidth / result.width).toFixed(2);

      result.height = +(containerWidth / originWidth * originHeight).toFixed(2);
    }
  }

  return result;
} // 计算上传进度（百分比）

function calculatePercent(total, loaded) {
  let percent = "0";

  if (total) {
    if (loaded === total) {
      percent = "99";
    } else {
      percent = (loaded / total * 100).toFixed(2);
    }
  }

  return `${percent}%`;
}
function createUploadMask() {
  const widget = document.createElement("span");
  widget.setAttribute("class", "ct-image");
  widget.setAttribute("data-type", "ct-progress");
  widget.innerHTML = `
    <img src="" />
    <div class="ct-image-mask image">
      <span class="ct-image-loading"></span>
      <span class="ct-image-progress">0%</span>
      <span class="ct-image-load-failed">抱歉，图片上传失败！<br style="line-height:0">如果是从外部拖动、粘贴过来的图片，请下载原图并重新上传。<br style="line-height:0">否则，请点击下方按钮进行重试。
      </span>
      <button class="ct-image-reupload">重试</button>
    </div>
  `;
  return widget;
}

/**
 * @description
 *  统一上传服务处理
 *  TODO 如何区分不同的文件类型，附件，svg，图片等
 *  uploadingDisplay, 创建完placeHolder自定义样式操作
 */
function uploader(constructor) {
  constructor.prototype.insertFiles = insertFiles;
  constructor.prototype.prepare = prepare;
  constructor.prototype.onprogress = onprogress;
  constructor.prototype.failed = failed;
  return constructor;
}

function insertFiles(files, style) {
  const customUpload = this.config.customUpload;
  const uploadCheck = this.config.customUploadCheck;
  return Promise.all(Array.from(files).map(async file => {
    if (!uploadCheck || (await uploadCheck(file))) {
      const key = uuid$1();
      this.prepare({
        key,
        file,
        style
      });
      const failedHandle = this.failed(key, file);
      return customUpload({
        file,
        onprogress: this.onprogress(key),
        insert: this.insertToDoc(key, file.name, failedHandle),
        failedHandle
      });
    }
  }));
}

function prepare({
  key,
  file,
  url,
  position,
  style
}) {
  const {
    state: {
      tr
    },
    dispatch
  } = this.view;
  const pos = typeof position === "number" ? position : tr.selection.from;
  const src = url || URL.createObjectURL(file);

  if (!tr.selection.empty) {
    tr.deleteSelection();
    dispatch(tr);
  }

  createPlaceholder(this.view, this.pluginName, key, pos);
  const placeholderInfo = findPlaceholder(this.view, this.pluginName, key);

  if (!placeholderInfo) {
    return;
  } // 上传时非图片自定义样式


  if (this.uploadingDisplay) {
    this.uploadingDisplay({
      key,
      file,
      placeholderInfo
    });
  } else {
    const {
      node
    } = placeholderInfo;
    const $img = node.querySelector("img");
    $img.src = src;

    if (style) {
      Object.entries(style).forEach(([name, rule]) => {
        $img.style[name] = rule;
      });
    }
  }

  return key;
}

function onprogress(key) {
  return ({
    total,
    loaded
  }) => {
    const placeholder = findPlaceholder(this.view, this.pluginName, key);

    if (!placeholder) {
      return;
    }

    placeholder.node.querySelector(".ct-image-progress").innerText = calculatePercent(total, loaded);
  };
}

function failed(key, file, customReupload) {
  return msg => {
    const placeholder = findPlaceholder(this.view, this.pluginName, key);

    if (!placeholder) {
      return;
    }

    const {
      node
    } = placeholder;
    const $progress = node.querySelector(".ct-image-progress");
    const $loading = node.querySelector(".ct-image-loading");
    const $loadFailed = node.querySelector(".ct-image-load-failed");
    const $btnUpload = node.querySelector(".ct-image-reupload");
    $loading.style.display = "none";
    $progress.style.display = "none";
    $loadFailed.style.display = "block";
    $btnUpload.style.display = "block";
    $btnUpload.addEventListener("click", () => {
      deletePlaceholder(this.view, this.pluginName, key);

      if (customReupload) {
        customReupload();
      } else {
        this.insertFiles([file]);
      }
    });
  };
}

const computeSliceMinWidth = slice => {
  let result = 0;
  slice.content.forEach(node => {
    const name = node.type.name;
    const braceWidth = node.type.schema.nodes[name].spec.braceWidth;
    const width = parseInt(node.attrs.width, 10) || braceWidth || 0;

    if (width > result) {
      result = width;
    }
  });
  return result;
};

const $CtLoading = (() => {
  const div = document.createElement("div");
  div.classList.add("ct-editor-loading");
  div.innerHTML = `<div class="loading-wrapper"><i></i><i></i><i></i></div>`;
  return div;
})();

function download(src, otherAttributes, useOpen) {
  const url = `${src}${src.indexOf('?') > -1 ? '&' : '?'}download=true`;

  if (useOpen) {
    window.open(url);
    return;
  }

  const aEl = document.createElement('a');
  aEl.setAttribute('href', url);

  if (typeof otherAttributes === 'string') {
    aEl.setAttribute('download', otherAttributes);
    aEl.download = otherAttributes;
  }

  if (typeof otherAttributes === 'object') {
    Object.keys(otherAttributes).forEach(attrKey => aEl.setAttribute(attrKey, otherAttributes[attrKey]));
  }

  aEl.click();
}

var css$3 = ".ct-toolbar-tooltip{position:absolute;left:50%;-webkit-transform:translate(-50%);transform:translate(-50%);bottom:-35px;height:25px;padding:0 10px;line-height:25px;white-space:nowrap;vertical-align:middle;color:#fff;font-size:12px;background:rgba(0,0,0,.7);border-radius:4px;z-index:1}.ct-toolbar-tooltip:after{position:absolute;width:0;top:-5px;right:50%;-webkit-transform:translate(50%);transform:translate(50%);display:block;border-color:rgba(0,0,0,.7) transparent;border-style:solid;border-width:0 5px 5px;content:\"\";z-index:1}.ct-input{border:1px solid #ddd;border-radius:2px}.ct-web-dangers-shadow-wrapper{width:100%;height:100%}.ct-image.ProseMirror-widget{vertical-align:bottom}.ct-img-failed{display:inline-block;width:100%;height:100%}:.ct-img-failed:before{content:\"\";position:absolute;left:0;top:0;display:block;width:100%;height:100%;background:#f6f6f6;z-index:1}.ct-img-failed:after{content:\"\";position:absolute;left:12px;top:12px;display:block;width:28px;height:20px;z-index:2;background:url(\"data:image/svg+xml;charset=utf-8,%3Csvg class='icon' viewBox='0 0 1380 1024' xmlns='http://www.w3.org/2000/svg' width='26.953' height='20'%3E%3Cdefs%3E%3Cstyle/%3E%3C/defs%3E%3Cpath d='M526.889 315.732l165.016 171.024a66.785 66.785 0 0 1 18.724 46.371V737.77l135.664-202.254-123.399-162.635a66.78 66.78 0 0 1-8.027-67.029L830.58 40.121C841.19 15.756 865.235.001 891.809.001h338.475c82.394 0 149.89 65.742 149.89 147.731V876.27c0 81.989-67.496 147.731-149.89 147.731H149.89C67.496 1024.001 0 958.259 0 876.27V147.732C0 65.743 67.496.001 149.89.001h393.258c45.99 0 78.216 45.4 63.044 88.815L526.89 315.733z' fill='%237F9496'/%3E%3Cpath d='M543.148 66.783l-83.824 239.855a44.52 44.52 0 0 0 9.99 45.602l174.532 180.887v277.792a44.52 44.52 0 0 0 19.721 36.974c20.42 13.697 48.078 8.247 61.775-12.173l185.264-276.199c10.592-15.791 9.987-36.565-1.506-51.712L776.096 332.515 891.809 66.784h338.475c45.899 0 83.107 36.242 83.107 80.949v728.538c0 44.707-37.208 80.949-83.107 80.949H149.89c-45.899 0-83.107-36.242-83.107-80.949V147.733c0-44.707 37.208-80.949 83.107-80.949h393.258z' fill='%23FFF'/%3E%3Cpath d='M496.955 289.391l252.816 398.046 196.047-178.035 300.791 358.771H133.566z' fill='%23DDD'/%3E%3Cpath d='M526.889 315.732l165.016 171.024a66.785 66.785 0 0 1 18.724 46.371V737.77l135.664-202.254-123.399-162.635a66.78 66.78 0 0 1-8.027-67.029L830.58 40.121C841.19 15.756 865.235.001 891.809.001h338.475c82.394 0 149.89 65.742 149.89 147.731V876.27c0 81.989-67.496 147.731-149.89 147.731H149.89C67.496 1024.001 0 958.259 0 876.27V147.732C0 65.743 67.496.001 149.89.001h393.258c45.99 0 78.216 45.4 63.044 88.815L526.89 315.733zm16.259-248.949H149.89c-45.899 0-83.107 36.242-83.107 80.949V876.27c0 44.707 37.208 80.949 83.107 80.949h1080.394c45.899 0 83.107-36.242 83.107-80.949V147.732c0-44.707-37.208-80.949-83.107-80.949H891.809L776.096 332.514 909.1 507.808c11.493 15.147 12.098 35.922 1.506 51.712L725.342 835.719c-13.697 20.42-41.355 25.871-61.775 12.173a44.522 44.522 0 0 1-19.721-36.974V533.126L469.314 352.239a44.523 44.523 0 0 1-9.99-45.602l83.824-239.855z' fill='%237F9496'/%3E%3Cpath d='M1230.284 309.628c0 67.06-55.813 121.423-124.661 121.423s-124.661-54.363-124.661-121.423 55.813-121.423 124.661-121.423 124.661 54.363 124.661 121.423z' fill='%23EEE'/%3E%3C/svg%3E\") no-repeat 0 0}.ct-editor-loading{display:inline-block;font-size:12px;position:absolute;bottom:0;left:0;right:0;top:0;z-index:9;text-align:center;white-space:nowrap;background:#f6f6f6}.ct-editor-loading .loading-wrapper{position:absolute;left:0;right:0;top:50%;height:-webkit-fit-content;height:-moz-fit-content;height:fit-content;-webkit-transform:translateY(-50%);transform:translateY(-50%)}.ct-editor-loading .loading-wrapper>i{display:none;vertical-align:middle}.ct-editor-loading .loading-wrapper>i:after,.ct-editor-loading .loading-wrapper>i:before{content:\" \";display:inline-block;height:100%;margin-left:-.25em;vertical-align:middle}.ct-editor-loading .loading-wrapper i:nth-child(-n+3){display:inline-block;width:1em;height:1em;background:#00a7cd;border-radius:50%;-webkit-animation:bounce-delay 1.4s ease-in-out infinite both;animation:bounce-delay 1.4s ease-in-out infinite both}.ct-editor-loading .loading-wrapper i:not(:last-child){margin-right:.4em}.ct-editor-loading .loading-wrapper i:first-child{-webkit-animation-delay:-.32s;animation-delay:-.32s}.ct-editor-loading .loading-wrapper i:nth-child(2){-webkit-animation-delay:-.16s;animation-delay:-.16s}@-webkit-keyframes bounce-delay{0%,80%,to{-webkit-transform:scale(0);transform:scale(0)}40%{-webkit-transform:scale(1);transform:scale(1)}}@keyframes bounce-delay{0%,80%,to{-webkit-transform:scale(0);transform:scale(0)}40%{-webkit-transform:scale(1);transform:scale(1)}}";
styleInject(css$3);

export { getContentWidth, findParentNode, findParentDOM, findParentBlockNode, setBetweenAttrs, getInsertBlockSlice, insertBlock, canInsertTypes, getBlockNode, isChild, getSystemInfo, isPrintableKey, compute, getTargetLeft, checkConfig, uuid$1 as uuid, fixSvgRatio, getSizeFromSvg, getSvgSizeAfterEdit, getDynamicSrc, copyToClipboard, mobileCopyToClipboard, handleBackspaceCreator, handleDeleteCreator, handleTabCreator, handleEnterCreator, uploadHelper, PopoverStore, createPlaceholder, findPlaceholder, deletePlaceholder, createShadowDOM, getElementFromDOM, send, resizer, calculateWidth, Popover, uploader, calculateSize, createUploadMask, computeSliceMinWidth, $CtLoading, download, config };
//# sourceMappingURL=index.js.map



// WEBPACK FOOTER //
// ./~/@mtfe/citadel-editor/es/utils/index.js